import {
  Alert,
  Badge,
  Button,
  Group,
  List,
  Paper,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AGENDA_KINDS,
  AGENDA_TEMPLATE_BODY_MAX,
  AGENDA_TITLE_MAX,
  type AgendaKind,
  type AgendaTemplate,
  type BudgetDisplay,
} from "@msc/shared";

import { useSegments } from "../../hooks/useSegments.js";
import {
  useAgendaTemplates,
  useDeleteAgendaOverride,
  useSaveAgendaTemplate,
} from "../../hooks/useSettings.js";
import { renderAgendaMarkdown } from "../../lib/customerView/agendaMarkdown.js";
import { dirtyTemplate } from "../../lib/customerView/agendaTemplateDirty.js";
import { describeApiError } from "../../lib/errorText.js";

/** Gleiche Obergrenzen wie `agendaTemplateSchema` in `@msc/shared`. */
const TITLE_MAX = AGENDA_TITLE_MAX;
const BODY_MAX = AGENDA_TEMPLATE_BODY_MAX;

/** Die fünf Platzhalter der Spec (`agendaPlaceholders.ts` in der API) — hier nur benannt, nicht ausgewertet. */
const PLACEHOLDERS: { token: string; labelKey: string }[] = [
  { token: "kunde", labelKey: "kunde" },
  { token: "datum", labelKey: "datum" },
  { token: "events", labelKey: "events" },
  { token: "wartung.tabelle", labelKey: "wartungTabelle" },
  { token: "budget.tabelle", labelKey: "budgetTabelle" },
];

/**
 * Vorlagenpflege der Customer-View-Agenda-Folien (D9): Bereichswahl
 * (Standard / je Bereich, dasselbe Muster wie die Vor-Ort-Frequenzen in
 * `FrequenciesSettings`, `routes/settings.tsx`), je Punkt Titel, Markdown-Text
 * und — nur bei „Budget" — die Anzeigeart.
 *
 * **Bewusst NICHT unter `/settings` gemountet.** Die Route verlangt
 * `RequireRole(["admin","bereichsleiter","stellvertreter"])`
 * (`router.ts`, Kommentar dort: „Inbox/Einstellungen liefern nur für
 * Admin/BL/Stellv. Inhalte"). D9 verlangt dagegen ausdrücklich „Vorlagen
 * pflegen alle Leads" — ein reiner `projektleiter` ohne eine der drei
 * weiten Rollen erreicht `/settings` gar nicht erst, egal was dort steht.
 * Die API teilt diese Einschätzung längst (`templateRoleGuard =
 * requireRole([...APP_ROLES])` in `customerView.routes.ts`, mit dem
 * ausdrücklichen Kommentar „alle Leads"); nur eine Platzierung unter
 * `/settings` hätte sie in der Oberfläche unterlaufen. Deshalb hängt dieses
 * Panel stattdessen in der Vorbereitung der Customer View (`AgendaPrep`,
 * Knopf „Vorlagen pflegen" öffnet es als Modal) — dorthin kommt jeder Lead,
 * der überhaupt einen Termin vorbereitet.
 */
export function AgendaTemplatesPanel() {
  const { t } = useTranslation();
  const { data: segments } = useSegments();
  // segmentId === null → globalen Standard zeigen, sonst den Override des Bereichs
  const [segmentId, setSegmentId] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useAgendaTemplates(segmentId);
  const templatesByKind = new Map((data?.templates ?? []).map((tpl) => [tpl.kind, tpl]));

  return (
    <Stack gap="md">
      <Select
        label={t("pages.settings.agendaTemplates.segmentLabel")}
        description={
          segmentId === null
            ? t("pages.settings.agendaTemplates.segmentDefaultDescription")
            : t("pages.settings.agendaTemplates.segmentOverrideDescription")
        }
        placeholder={t("pages.settings.agendaTemplates.segmentDefaults")}
        data={[
          { value: "__default__", label: t("pages.settings.agendaTemplates.segmentDefaults") },
          ...(segments ?? []).map((s) => ({ value: s.id, label: s.name })),
        ]}
        value={segmentId === null ? "__default__" : segmentId}
        onChange={(v) => setSegmentId(v === "__default__" || v === null ? null : v)}
        style={{ maxWidth: 320 }}
        clearable={false}
      />

      <Alert color="blue" variant="light" title={t("pages.settings.agendaTemplates.placeholdersTitle")}>
        <List size="sm" spacing={2}>
          {PLACEHOLDERS.map((p) => (
            <List.Item key={p.token}>
              <Text component="span" fw={600} ff="monospace">
                {`{{${p.token}}}`}
              </Text>{" "}
              {t(`pages.settings.agendaTemplates.placeholder.${p.labelKey}`)}
            </List.Item>
          ))}
        </List>
      </Alert>

      {isError && (
        <Alert color="red" title={t("common.error")}>
          {describeApiError(error, t)}
        </Alert>
      )}

      {isLoading ? (
        <Skeleton height={280} />
      ) : (
        <Stack gap="md">
          {AGENDA_KINDS.map((kind) => (
            <AgendaTemplateEditor
              key={kind}
              segmentId={segmentId}
              kind={kind}
              template={templatesByKind.get(kind) ?? null}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function AgendaTemplateEditor({
  segmentId,
  kind,
  template,
}: {
  segmentId: string | null;
  kind: AgendaKind;
  template: AgendaTemplate | null;
}) {
  const { t } = useTranslation();
  const save = useSaveAgendaTemplate();
  const del = useDeleteAgendaOverride();

  const [title, setTitle] = useState(template?.title ?? "");
  const [body, setBody] = useState(template?.body ?? "");
  const [budgetDisplay, setBudgetDisplay] = useState<BudgetDisplay>(template?.budgetDisplay ?? "euro");
  const [previewOpen, setPreviewOpen] = useState(false);

  // Neu geladene Vorlage (Bereichswechsel oder nach dem Speichern/Zurücksetzen)
  // übernehmen — Änderungen des Leads leben nur bis zum nächsten Laden dieser
  // Zeile, es gibt hier (anders als bei den Termin-Agenda-Punkten) keinen
  // eigenen Zwischenspeicher, den es zu schützen gälte.
  useEffect(() => {
    setTitle(template?.title ?? "");
    setBody(template?.body ?? "");
    setBudgetDisplay(template?.budgetDisplay ?? "euro");
    setPreviewOpen(false);
  }, [template?.title, template?.body, template?.budgetDisplay, segmentId, kind]);

  const dirty = template
    ? dirtyTemplate(
        { title: template.title, body: template.body, budgetDisplay: template.budgetDisplay },
        { title, body, budgetDisplay },
      )
    : false;

  async function handleSave() {
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) return;
    try {
      await save.mutateAsync({ segmentId, kind, title: trimmedTitle, body, budgetDisplay });
      notifications.show({ color: "green", message: t("pages.settings.agendaTemplates.saveSuccess") });
    } catch (err) {
      notifications.show({ color: "red", message: describeApiError(err, t) });
    }
  }

  async function handleReset() {
    if (segmentId === null) return;
    try {
      await del.mutateAsync({ segmentId, kind });
      notifications.show({ color: "green", message: t("pages.settings.agendaTemplates.resetSuccess") });
    } catch (err) {
      notifications.show({ color: "red", message: describeApiError(err, t) });
    }
  }

  const preview = previewOpen ? renderAgendaMarkdown(body) : undefined;

  return (
    <Paper withBorder p="sm">
      <Stack gap="xs">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Group gap="xs">
            <Title order={5}>{t(`pages.settings.agendaTemplates.kind.${kind}`)}</Title>
            <Badge color={template?.isOverride ? "blue" : "gray"} variant="light">
              {template?.isOverride
                ? t("pages.settings.agendaTemplates.override")
                : t("pages.settings.agendaTemplates.standard")}
            </Badge>
          </Group>
          <Group gap="xs">
            <Button
              size="compact-xs"
              variant={preview === undefined ? "default" : "light"}
              onClick={() => setPreviewOpen((v) => !v)}
            >
              {preview === undefined
                ? t("pages.settings.agendaTemplates.previewShow")
                : t("pages.settings.agendaTemplates.previewHide")}
            </Button>
            {segmentId !== null && template?.isOverride && (
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                loading={del.isPending}
                onClick={() => void handleReset()}
              >
                {t("pages.settings.agendaTemplates.resetButton")}
              </Button>
            )}
            <Button
              size="compact-xs"
              disabled={!dirty || title.trim().length === 0}
              loading={save.isPending}
              onClick={() => void handleSave()}
            >
              {t("pages.settings.agendaTemplates.saveButton")}
            </Button>
          </Group>
        </Group>

        <TextInput
          label={t("pages.settings.agendaTemplates.titleLabel")}
          maxLength={TITLE_MAX}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />

        {kind === "budget" && (
          <SegmentedControl
            value={budgetDisplay}
            onChange={(v) => setBudgetDisplay(v as BudgetDisplay)}
            data={[
              { label: t("pages.settings.agendaTemplates.budgetEuro"), value: "euro" },
              { label: t("pages.settings.agendaTemplates.budgetRestprozent"), value: "restprozent" },
            ]}
          />
        )}

        <Textarea
          label={t("pages.settings.agendaTemplates.bodyLabel")}
          autosize
          minRows={4}
          maxRows={16}
          maxLength={BODY_MAX}
          value={body}
          styles={{ input: { fontFamily: "monospace", fontSize: "0.8rem" } }}
          onChange={(e) => setBody(e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          {t("pages.settings.agendaTemplates.characters", { n: body.length, max: BODY_MAX })}
        </Text>

        {preview !== undefined && (
          <Paper withBorder p="xs" bg="var(--msc-flaeche)">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
              {t("pages.settings.agendaTemplates.previewLabel")}
            </Text>
            {/* Bereits sanitisiert (`renderAgendaMarkdown`). */}
            <div className="agenda-preview" dangerouslySetInnerHTML={{ __html: preview }} />
          </Paper>
        )}
      </Stack>
    </Paper>
  );
}
